<template>
  <div :class="$style.container">
    <div :class="$style.controlsContainer">
      <ActivityLogViews
        :columnNames="columnNames"
        :value="selectedView"
        @input="handleViewChange"
      />
      <div :class="$style.datePickerContainer">
        <ActivityLogDatePicker @change="loadInitialData" />
        <FlatActionButton
          :action="loadInitialData"
          :class="$style.controlsButton"
          :loading="isRefreshing"
        >
          refresh
        </FlatActionButton>
      </div>
    </div>
    <div :class="$style.filtersContainer">
      <InputSearch
        v-if="$hasFeatureFlag($featureFlags.diEventsSearch)"
        v-model="searchQuery"
        :class="$style.filterSearch"
        :disabled="isRefreshing"
        placeholder="Search"
        @input="triggerSearch"
      />
      <ActivityLogFilters
        :class="$style.activityLogFilters"
        :errorText="errorText"
        @add="handleFilterAdd"
        @change="(field, value) => handleFilterChange(false, field, value)"
        @clearError="handleClearFilterError"
        @clearFilters="() => handleFilterChange(true)"
      />
    </div>
    <div :class="$style.chartContainer">
      <ActivityLogChart
        :searchQuery="searchQuery"
        @datechange="loadInitialData"
      />
    </div>
    <div :class="$style.tableContainer">
      <div :class="$style.tableHeader">
        <span>
          {{ eventsCount >= 0 ? eventsCountText : '' }}
        </span>
        <div>
          <DropDownMultiSelectSearch
            v-model="columnNames"
            :class="$style.columnsDropDown"
            :disabled="eventsCount <= 0"
            :displayValue="false"
            :maxOptionsSelectable="8"
            name="columns"
            :options="columnOptions"
            :sortOptions="false"
            :useDarkLinkStyleForButton="true"
            @closed="handleColumnsChange"
            @input="clearQuickView"
          >
            <template v-slot:footer>
              <div>
                <a
                  :class="$style.revertColumnsLink"
                  @click="setDefaultColumns('all')"
                >
                  Revert to default columns
                </a>
              </div>
            </template>
          </DropDownMultiSelectSearch>
          <ExportDropDown
            :disabled="isExportDisabled"
            :exportFunction="exportData"
            @error="(props) => triggerExportSegmentEvent({ ...props, success: false })"
            @success="(props) => triggerExportSegmentEvent({ ...props, success: true })"
          />
        </div>
      </div>
      <InsightsEventsTable
        ref="tableContainer"
        :class="$style.table"
        :columns="columns"
        :isRefreshing="isRefreshing"
        :loadingAdditionalData="loadingAdditionalData"
        :moreToLoad="moreToLoad"
        :tableData="tableData"
        @filterChange="handleFilterChange"
        @loadMore="loadAdditionalData"
        @restoreDefaults="restoreTableDefaults"
      />
      <ProgressModal
        :canCancel="true"
        headerText="Exporting Activity Log..."
        mainActionText="cancel export"
        :showCount="false"
        @cancel="cancelExport"
      />
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { InputSearch } from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { debounce, get, intersection } from 'lodash';
import {
  mapActions,
  mapState,
} from 'vuex';
import { notify } from 'client/util';
import { quickPicksMetadata, quickPicksOptions } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import ActivityLogChart from 'client/Insights/ActivityLog/ActivityLogChart.vue';
import ActivityLogDatePicker from 'client/Insights/ActivityLog/ActivityLogDatePicker.vue';
import ActivityLogFilters from 'client/Insights/ActivityLog/ActivityLogFilters.vue';
import ActivityLogViews from 'client/Insights/ActivityLog/ActivityLogViews/ActivityLogViews.vue';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventMetadata, {
  eventFields,
  eventServiceMetadata,
  formatEventValue,
} from 'client/Insights/ActivityLog/constants/EventMetadata';
import ExportDropDown from 'components/DropDownMenu/ExportDropDown.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import InsightsEventsTable from 'client/Insights/ActivityLog/InsightsEventsTable.vue';
import ProgressModal from 'components/Modal/ProgressModal.vue';
import pluralize from 'pluralize';

const { ItemTypeConsts, StoreModuleMap } = Constants;
const { error } = Constants.default;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];

const defaultColumnNames = [
  eventFields.timestamp,
  eventFields.eventType,
  eventFields.result,
  eventFields.initiatedBy,
  eventFields.success,
  eventFields.geoip,
];

export default {
  name: 'ActivityLog',
  components: {
    ActivityLogChart,
    ActivityLogDatePicker,
    ActivityLogFilters,
    ActivityLogViews,
    ExportDropDown,
    DropDownMultiSelectSearch,
    FlatActionButton,
    InputSearch,
    InsightsEventsTable,
    ProgressModal,
  },

  data: () => ({
    columnNames: [...defaultColumnNames],
    errorText: {
      event_type: '',
      service: '',
    },
    eventsCount: -1,
    isRefreshing: false,
    loadingAdditionalData: false,
    moreToLoad: true,
    rowsPerRequest: 50,
    searchAfter: [],
    searchQuery: '',
    tableData: [],
  }),

  computed: {
    ...mapState(insightsEventModule, [
      'endDate',
      'filters',
      'quickPickDate',
      'selectedView',
      'startDate',
    ]),

    columnOptions() {
      return Object.values(eventFields)
        .filter((field) => {
          if (EventMetadata[field]) {
            return EventMetadata[field].getDataValue;
          }
          return false;
        })
        .sort()
        .map(field => ({
          name: field,
          value: field,
          required: field === eventFields.timestamp,
        }));
    },

    columns() {
      // Make sure we're showing '-' if no data
      return this.columnNames.map((columnName) => {
        const column = EventMetadata[columnName];
        return {
          ...column,
          getDataValue: d => formatEventValue(column.getDataValue(d)),
        };
      });
    },

    eventsCountText() {
      return `${this.eventsCount.toLocaleString()} ${pluralize('event', this.eventsCount)}`;
    },

    isExportDisabled() {
      return this.isRefreshing || !this.tableData.length;
    },
  },

  created() {
    if (!this.startDate && !this.quickPickDate) {
      // Default time range to one hour if it has never been set
      this.setQuickPickDate(quickPicksOptions.oneHour);
    }

    this.loadInitialData();

    // Set default columns when service filter is pre-selected on page load
    const serviceFilter = this.filters.find(filter => filter.field === eventFields.service);
    this.setDefaultColumns(serviceFilter.value[0]);
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'addFilter',
      'cancelExport',
      'clearFilters',
      'exportData',
      'fetchDistinctValues',
      'fetchEventsCount',
      'fetchEventsData',
      'refreshDates',
      'setFilter',
      'setQuickPickDate',
      'setSelectedView',
    ]),

    clearQuickView() {
      // Only reset the view if the user is in a selected quick view
      if (this.selectedView && this.selectedView.isQuickView) {
        this.setSelectedView(null);
      }
    },

    fetchData(searchAfter) {
      const payload = {
        limit: this.rowsPerRequest,
        q: this.searchQuery,
      };

      if (searchAfter) payload.search_after = searchAfter;

      return this.fetchEventsData(payload)
        .then((resp) => {
          // Check if there may be additional data to load
          this.moreToLoad = (resp.resultCount === resp.limit);

          // Store the searchAfter to load additional data when requested
          this.searchAfter = resp.searchAfter;

          if (!(resp.data instanceof Array)) {
            throw new Error();
          }

          return resp.data;
        });
    },

    handleClearFilterError(field) {
      this.$set(this.errorText, field, '');
    },

    handleColumnsChange() {
      EventHelper.triggerActivityLogColumnsChanged({ columns: this.columnNames });
    },

    handleFilterAdd() {
      this.clearQuickView();
      this.loadFilterOptions();
    },

    handleFilterChange(clearFilters = false, field, value) {
      if (clearFilters) {
        this.clearFilters();
        this.setDefaultColumns('all');
      }

      this.clearQuickView();

      if (field === eventFields.service) {
        this.setDefaultColumns(value[0]);
      }

      this.loadInitialData({ refreshFilterOptions: false });
    },

    handleViewChange(view) {
      this.setSelectedView(view);

      // Clear and add filters; Correct filter values will be selected when loading filter options
      this.clearFilters();
      if (view && view.filters) {
        view.filters.forEach(({ field, value }) => {
          this.setFilter({ field, value });
        });
      }

      // Set columns to match the view
      if (view && view.columns) {
        this.columnNames = [...view.columns];
      } else if (view && view.additionalColumns) {
        this.columnNames = defaultColumnNames.concat(view.additionalColumns);
      } else {
        this.setDefaultColumns('all');
      }

      // Set range
      if (view && view.range) {
        const quickPick = Object.values(quickPicksMetadata)
          .find(qp => qp.referenceValue === view.range);

        if (quickPick) {
          this.setQuickPickDate(quickPick.name);
        }
      }

      this.loadInitialData();
    },

    loadAdditionalData() {
      this.loadingAdditionalData = true;
      return this.fetchData(this.searchAfter)
        .then((data) => {
          this.tableData = this.tableData.concat(data);
        })
        .catch((err) => {
          notify(error, get(err, 'response.data.message', 'There was a problem retrieving events data.'));
        })
        .finally(() => {
          this.loadingAdditionalData = false;
        });
    },

    loadFilterOptions() {
      const fetchPromises = this.filters
        // No need to fetch `service` filter options. They are hardcoded.
        .filter(({ field }) => field !== eventFields.service)
        .map(({ field }) => {
          this.handleClearFilterError(field);

          return this.fetchDistinctValues({ field })
            .then((options) => {
              this.updateFilterBasedOnDistinctValues(field, options);
              EventHelper.triggerActivityLogFilterOptionsLoaded({
                numOptionsLoaded: options.length,
                numOptionsNotLoaded: 0,
              });
            })
            .catch((err) => {
              this.errorText[field] = err.message;
            });
        });
      return Promise.allSettled(fetchPromises);
    },

    async loadInitialData(params = {}) {
      const { refreshFilterOptions = true } = params;

      this.isRefreshing = true;

      // Scroll the table container to the top after loading new set of data
      if (this.$refs.tableContainer) {
        this.$refs.tableContainer.$el.scrollTop = 0;
      }

      // Set start and end date so the values in the datepicker inputs reflect the current view
      this.refreshDates();

      // Fetch filter options so we can remove any pre-set filter options that aren't available
      if (refreshFilterOptions) await this.loadFilterOptions();

      // Load new set of data
      this.fetchData()
        .then((data) => {
          this.tableData = data;
        })
        .catch((err) => {
          notify(error, get(err, 'response.data.message', 'There was a problem retrieving events data.'));
        })
        .finally(() => {
          this.isRefreshing = false;
        });

      // Asynchronuously populate events count
      this.fetchEventsCount({ q: this.searchQuery })
        .then((resp) => {
          this.eventsCount = get(resp, 'data.count', -1);
        })
        .catch((err) => {
          this.eventsCount = -1; // Negative number will set count to not show
          notify(error, get(err, 'response.data.message', 'There was a problem retrieving events count.'));
        });
    },

    restoreTableDefaults() {
      this.setSelectedView(null);
      this.handleFilterChange(true);
    },

    // set default columns to 'all' filter event if no service is selected for error handling
    setDefaultColumns(selectedService = 'all') {
      this.columnNames = eventServiceMetadata[selectedService].defaultColumns;
    },

    triggerExportSegmentEvent(props = {}) {
      EventHelper.triggerActivityLogExported({
        endDate: this.endDate,
        startDate: this.startDate,
        ...(this.quickPickDate && {
          quickPickDate: quickPicksMetadata[this.quickPickDate].displayName,
        }),
        ...props,
      });
    },

    triggerSearch: debounce(function handle() {
      this.loadInitialData();

      if (this.searchQuery) {
        UiEvents.triggerInputEntered({
          description: 'Directory Insights search',
          displayLogic: 'Non-empty input',
          page: this.$page,
          section: 'Filters section',
          text: 'Search',
          value: '(Redacted for security)',
        });
      }
    }, 250),

    updateFilterBasedOnDistinctValues(field, options) {
      const filter = this.filters.find(d => d.field === field);
      if (filter) {
        const optionValues = options.map(d => d.value);
        if (this.selectedView) {
          const viewFilter = this.selectedView.filters.find(d => d.field === field);
          if (viewFilter) {
            // If there are options that match the view filter value, set the filter to those.
            // Otherwise, force the filter to use the view filter value.
            const sharedValues = intersection(viewFilter.value, optionValues);
            this.setFilter({
              field,
              value: sharedValues.length ? sharedValues : viewFilter.value,
            });
          }
        } else {
          // Merge the filter options currently selected with the new set of options available
          const value = intersection(filter.value, optionValues);
          this.setFilter({ field, value });
        }
      }
    },
  },
};
</script>

<style module>
@value (gradientDark2, gradientLight6) from '~css/colors.css';

.controlsContainer {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.datePickerContainer {
  display: flex;
  margin-left: 10px;
}

.filtersContainer {
  display: flex;
  margin-top: 10px;
  z-index: 3;
}

.filterSearch {
  margin-right: 8px;
  width: 50% !important;
}

.activityLogFilters {
  min-width: 600px;
  padding-top: 4px;
}

.chartContainer {
  margin-top: 12px;
  z-index: 2;
}

.controlsButtonContainer {
  margin-top: 10px;
  text-align: right;
  white-space: nowrap;
  z-index: 2;
}

.controlsButton {
  align-self: flex-end;
  margin-bottom: 2px !important;
  margin-left: 8px !important;
}

.dateText { font-style: italic; }

.tableHeader {
  align-items: center;
  background-color: white;
  border: 1px solid gradientLight6;
  border-bottom: none;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  display: flex;
  font-size: 13px;
  font-weight: bold;
  justify-content: space-between;
  padding: 10px 20px;
}

.tableContainer {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 14px 0;
  max-height: 100%;
  z-index: 1;
}

.tableContainer > .table {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.columnsDropDown {
  display: inline-block;
  font-weight: 400;
  margin-right: 16px;
  vertical-align: middle;
}

.revertColumnsLink { cursor: pointer; }
</style>
